import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { CustomerPolicyPatch } from '@ca/common'
import FormCheckboxSectionRow from '@ui/forms/Checkbox/FormCheckboxSectionRow'
import { useCustomerPolicies } from './useCustomerPolicies'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import useDateFormat from '@ui/hooks/useDateFormat'
import useModalDataState from 'features/order/hooks/useModalDataState'
import { useSession } from 'hooks/useSession'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form'
import { markDetailSectionSaved } from '@user/customer/confirmation/markDetailSectionSaved'
import PolicyReadModal from './PolicyReadModal'

interface Props {
  open: boolean
  close: () => void
}

const requiredPolicyRules: RegisterOptions<Record<string, boolean>, string> = {
  validate: (value) => value === true || 'required',
}

export default function PoliciesModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'deals-modals', 'auth', 'validation'])
  const formatDate = useDateFormat({ dateStyle: 'medium' })
  const setInfo = useSession((s) => s.setInfo)
  const contentPolicyModal = useModalDataState<string | null>({ data: null })
  const {
    policies,
    isPolicyAccepted,
    getPolicyAcceptedAt,
    patchPolicies,
    isSaving,
  } = useCustomerPolicies()
  const { shouldShowPoliciesError } = useConfirmCustomer()
  const policyDefaultValues = useMemo(
    () =>
      Object.fromEntries(
        policies.map((policy) => [policy.key, !!policy.acceptedAt])
      ) as Record<string, boolean>,
    [policies]
  )
  const form = useForm<Record<string, boolean>>({
    defaultValues: policyDefaultValues,
  })
  const values = useWatch({
    control: form.control,
  })
  const requiredPolicyKeys = policies
    .filter((policy) => policy.required)
    .map((policy) => policy.key)

  useEffect(() => {
    if (!open) {
      return
    }
    form.reset(policyDefaultValues)
  }, [open, form, policyDefaultValues])

  useEffect(() => {
    if (!open || !shouldShowPoliciesError || requiredPolicyKeys.length === 0) {
      return
    }
    form.trigger(requiredPolicyKeys)
  }, [open, shouldShowPoliciesError, requiredPolicyKeys, form])

  const handleClose = () => {
    contentPolicyModal.close()
    form.reset()
    close()
  }

  const handleTogglePolicy = (policyKey: string) => {
    contentPolicyModal.close()
    const currentValue = !!form.getValues(policyKey)
    form.setValue(policyKey, !currentValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const handleSaveValid = async (formValues: Record<string, boolean>) => {
    const patch = Object.fromEntries(
      policies.flatMap((policy) => {
        const isAccepted = isPolicyAccepted(policy.key, policy.revision)
        const nextValue = !!formValues[policy.key]
        if (nextValue === isAccepted) {
          return []
        }
        return [[policy.key, nextValue] as const]
      })
    ) as CustomerPolicyPatch
    if (Object.keys(patch).length === 0) {
      handleClose()
      return
    }
    const isSuccess = await patchPolicies(patch)
    if (isSuccess) {
      markDetailSectionSaved('policies')
      handleClose()
    }
  }

  const handleSaveInvalid = () => {
    // Keep existing error highlighting behavior tied to this flag.
    setInfo({ isCustomerDetailsConfirmed: false })
  }

  const handleSave = form.handleSubmit(handleSaveValid, handleSaveInvalid)

  const isListOpen = open
  const isContentOpen = open && contentPolicyModal.isOpen

  return (
    <>
      <ModalBottom
        open={isListOpen}
        close={handleClose}
        center
        title={t('modals:policies.title')}
        desc={t('modals:policies.desc')}
        body={
          <FormProvider {...form}>
            <View className="gap-8 px-16">
              {policies.map((policy) => {
                const draftAccepted = !!values[policy.key]
                const acceptedAt = getPolicyAcceptedAt(
                  policy.key,
                  policy.revision
                )
                const acceptedAtInfo =
                  draftAccepted && acceptedAt
                    ? t('modals:policies.acceptedAt', {
                        date: formatDate(acceptedAt),
                      })
                    : undefined
                return (
                  <FormCheckboxSectionRow
                    key={policy.key}
                    name={policy.key}
                    rules={policy.required ? requiredPolicyRules : undefined}
                    onValueChange={() => contentPolicyModal.close()}
                    required={policy.required}
                    title={t(`deals-modals:policies.${policy.key}.title`)}
                    info={acceptedAtInfo}
                    actionComponent={
                      <Button
                        size="xs"
                        variant="outlined"
                        label={t('ui:read')}
                        onPress={() => contentPolicyModal.open(policy.key)}
                      />
                    }
                  />
                )
              })}
            </View>
          </FormProvider>
        }
        buttons={
          <>
            <Button
              variant="outlined"
              label={t('ui:cancel')}
              onPress={handleClose}
              disabled={isSaving}
            />
            <Button
              variant="filled"
              label={t('ui:save')}
              loading={isSaving}
              onPress={handleSave}
            />
          </>
        }
      />
      <PolicyReadModal
        open={isContentOpen}
        close={contentPolicyModal.close}
        policy={
          contentPolicyModal.data
            ? policies.find((p) => p.key === contentPolicyModal.data) ?? null
            : null
        }
        isDraftAccepted={!!values[contentPolicyModal.data ?? '']}
        onAccept={() => {
          if (!contentPolicyModal.data) {
            return
          }
          handleTogglePolicy(contentPolicyModal.data)
        }}
        formatDate={formatDate}
      />
    </>
  )
}
